// src/pages/Admin/CIODashboard.js
// CIO Admin Dashboard (Inbox + Detail/Reply PDF + Close/Reject with Reason)

import React, { useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import { db, storage, auth } from "../../firebaseConfig";
import {
  collection, getDocs, orderBy, query, where, updateDoc, doc, addDoc, serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";

/* ---------------- helpers ---------------- */

// remote image → dataURL (CORS safe if URL is public)
async function fetchToDataURL(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: "cors", cache: "no-cache" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/* simple reply PDF (letterhead optional) */
async function makeReplyPDF({ letterheadUrl, requestId, dateStr, toName, subject, body, officer }) {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();

  let y = 40;

  // try to add letterhead image (as dataURL)
  if (letterheadUrl) {
    try {
      const dataURL = await fetchToDataURL(letterheadUrl);
      if (dataURL) {
        const typeGuess = /\.jpe?g($|\?)/i.test(letterheadUrl) ? "JPEG" : "PNG";
        pdf.addImage(dataURL, typeGuess, 0, 0, W, H); // full-page letterhead (with its own margins)
        y = 120; // push content below header band
      }
    } catch { /* ignore image failure */ }
  }

  pdf.setFont("helvetica", "bold"); pdf.setFontSize(14);
  pdf.text("CIO REPLY", 40, y); y += 20;

  pdf.setFont("helvetica", "normal"); pdf.setFontSize(11);
  pdf.text(`Request ID: ${requestId}`, 40, y); y += 16;
  pdf.text(`Date: ${dateStr}`, 40, y); y += 22;
  pdf.text(`To: ${toName}`, 40, y); y += 16;
  pdf.text(`Subject: ${subject}`, 40, y); y += 18;

  const lines = pdf.splitTextToSize(String(body || "").trim() || "—", W - 80);
  lines.forEach((ln) => { pdf.text(ln, 40, y); y += 16; });

  y += 18;
  pdf.text(officer?.name || "CIO Officer", 40, y);
  if (officer?.designation) pdf.text(officer.designation, 40, y + 14);

  return pdf; // return jsPDF instance
}

function StatusBadge({ s }) {
  const cls =
    s === "Closed"      ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    s === "In Progress" ? "bg-amber-50  text-amber-800  border-amber-200"    :
    s === "Rejected"    ? "bg-rose-50   text-rose-800   border-rose-200"     :
                          "bg-slate-50  text-slate-700  border-slate-200";
  return <span className={`text-xs px-2 py-1 rounded-full border ${cls}`}>{s}</span>;
}
function KpiTile({ label, val, tone="slate" }) {
  const map = {
    indigo:"bg-indigo-50 text-indigo-800 border-indigo-200",
    emerald:"bg-emerald-50 text-emerald-800 border-emerald-200",
    amber:"bg-amber-50 text-amber-800 border-amber-200",
    rose:"bg-rose-50 text-rose-800 border-rose-200",
    slate:"bg-slate-50 text-slate-800 border-slate-200",
  };
  return (
    <div className={`rounded-xl border px-3 py-2 text-sm ${map[tone]}`}>
      <div>{label}</div><div className="text-xl font-extrabold">{val}</div>
    </div>
  );
}

/* ---------------- Main ---------------- */
export default function CIODashboard() {
  // Admin guard
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);

  // Dashboard
  const [status, setStatus] = useState("New");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthed(!!user);
      setChecking(false);
      if (!user) {
        try { window.history.replaceState({}, "", "/admin-login"); } catch {}
      }
    });
    return unsub;
  }, []);

  // Loader
  async function load(curStatus = status) {
    if (!authed) return;
    const qy = curStatus === "All"
      ? query(collection(db,"requests"), orderBy("dateSubmitted","desc"))
      : query(collection(db,"requests"), where("status","==",curStatus), orderBy("dateSubmitted","desc"));
    const snap = await getDocs(qy);
    setRows(snap.docs.map(d => ({ _id: d.id, ...d.data() })));
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, authed]);

  const metrics = useMemo(() => {
    const total   = rows.length;
    const closed  = rows.filter(x => x.status === "Closed").length;
    const pending = rows.filter(x => x.status === "In Progress").length;
    const risks   = rows.filter(x => x.status !== "Closed" && x.dueDate && new Date(x.dueDate) < new Date()).length;
    return { total, closed, pending, risks };
  }, [rows]);

  async function changeStatus(rec, to) {
    if (!authed) return;
    setBusy(true);
    try {
      await updateDoc(doc(db,"requests",rec._id), { status: to });
      await addDoc(collection(db,"requests",rec._id,"audits"), {
        action: `status:${to}`,
        by: auth?.currentUser?.email || "admin",
        at: serverTimestamp()
      });
      await load(to);
      setOpen({ ...rec, status: to });
    } finally { setBusy(false); }
  }

  if (checking) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <div className="bg-white border rounded-xl px-6 py-4 shadow">Checking access…</div>
      </div>
    );
  }
  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <div className="bg-white border rounded-xl px-6 py-6 shadow text-center">
          <div className="text-xl font-bold mb-2">Access Denied</div>
          <div className="text-sm text-slate-600">
            Please <a className="text-indigo-700 underline" href="/admin-login">login as admin</a> to continue.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r">
        <div className="h-16 flex items-center px-4 border-b">
          <div className="text-xl font-extrabold text-indigo-700">CIO Admin</div>
        </div>
        <div className="p-3 space-y-2 text-sm">
          {["New","In Progress","Closed","Rejected","All"].map(t => (
            <button key={t} onClick={()=>setStatus(t)}
              className={`w-full text-left px-3 py-2 rounded ${status===t?"bg-indigo-600 text-white":"hover:bg-slate-100"}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="mt-auto p-3 text-xs text-slate-500">Risks: {metrics.risks}</div>
      </aside>

      {/* Main */}
      <main className="flex-1">
        <div className="h-16 bg-white border-b px-4 flex items-center justify-between">
          <div className="font-bold">Requests — {status}</div>
          <div className="flex gap-2">
            <KpiTile label="Total"   val={metrics.total}/>
            <KpiTile label="Closed"  val={metrics.closed} tone="emerald"/>
            <KpiTile label="Pending" val={metrics.pending} tone="amber"/>
            <KpiTile label="Risks"   val={metrics.risks}   tone="rose"/>
            <button onClick={()=>load()} className="px-3 py-2 rounded border">Refresh</button>
          </div>
        </div>

        <div className="p-4">
          <div className="bg-white rounded-xl border overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-600">
                  <th className="py-2 px-3">Request ID</th>
                  <th>Member</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Due</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map(r => (
                  <tr key={r._id}>
                    <td className="py-2 px-3">{r.requestId}</td>
                    <td>{r.memberName}</td>
                    <td className="max-w-[340px] truncate">{r.subject}</td>
                    <td>{r.category}</td>
                    <td><StatusBadge s={r.status}/></td>
                    <td>{r.dueDate ? new Date(r.dueDate).toLocaleDateString() : "-"}</td>
                    <td className="text-right pr-3">
                      <button onClick={()=>setOpen(r)} className="px-2 py-1 rounded border text-xs hover:bg-slate-50">Open</button>
                    </td>
                  </tr>
                ))}
                {!rows.length && <tr><td className="py-6 text-center text-slate-500" colSpan={7}>No records</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Drawer */}
      {open && (
        <RequestDrawer rec={open} busy={busy} setBusy={setBusy} onClose={()=>setOpen(null)} onStatus={changeStatus}/>
      )}
    </div>
  );
}

/* ---------------- Decision Modal (Close / Reject with Reason) ---------------- */
function DecisionModal({ type, open, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [publicMsg, setPublicMsg] = useState("");

  if (!open) return null;
  const heading = type === "close" ? "Close Request" : "Reject Request";
  const okText = type === "close" ? "Close Case" : "Reject Case";

  return (
    <div className="fixed inset-0 z-[1100] flex items-start justify-center bg-black/50"
         style={{ paddingTop: "var(--header-total,148px)" }}>
      <div className="bg-white w-full max-w-lg rounded-xl shadow-xl border p-5">
        <div className="text-lg font-bold mb-2">{heading}</div>
        <p className="text-xs text-slate-600 mb-3">
          कृपया reason दर्ज करें। यह reason public portal पर भी दिखेगा (नीचे Public Message अलग से डाल सकते हैं)।
        </p>

        <label className="text-xs">Internal Reason (required)</label>
        <textarea className="w-full border rounded px-3 py-2 mb-3" rows={3}
                  value={reason} onChange={e=>setReason(e.target.value)} />

        <label className="text-xs">Public Message (optional)</label>
        <textarea className="w-full border rounded px-3 py-2 mb-4" rows={3}
                  placeholder="This reply is issued as per records available…"
                  value={publicMsg} onChange={e=>setPublicMsg(e.target.value)} />

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 rounded border">Cancel</button>
          <button
            onClick={()=>onSubmit({ reason: reason.trim(), publicMsg: publicMsg.trim() })}
            className={`px-3 py-2 rounded text-white ${type==="close" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
            disabled={!reason.trim()}
          >
            {okText}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Detail/Reply Drawer ---------------- */
function RequestDrawer({ rec, busy, setBusy, onClose, onStatus }) {
  const [letterheadUrl, setLetterheadUrl] = useState("");
  const [remarks, setRemarks] = useState("");
  const [body, setBody] = useState(
    "Sir/Madam,\n\nWith reference to the above request, please find the attached information as per records available. This reply is issued under Sec. 106 / Rule 35 of MSCS Act & Rules.\n\nRegards,\nCIO"
  );

  const [decisionType, setDecisionType] = useState(null); // "close" | "reject" | null
  const openDecision = (t) => setDecisionType(t);
  const closeDecision = () => setDecisionType(null);

  // Close/Reject submit
  async function submitDecision({ reason, publicMsg }) {
    try {
      setBusy(true);
      const finalType = decisionType === "close" ? "Closed" : "Rejected";
      const by = auth?.currentUser?.email || "admin";

      await updateDoc(doc(db, "requests", rec._id), {
        status: finalType,
        decision: {
          type: finalType.toLowerCase(),
          reason,
          publicMsg: publicMsg || reason,
          decidedBy: by,
          decidedAt: new Date().toISOString(),
        },
        publicStatus: finalType,
        publicNote: publicMsg || reason,
      });

      await addDoc(collection(db, "requests", rec._id, "audits"), {
        action: `decision:${finalType.toLowerCase()}`,
        reason,
        publicMsg: publicMsg || reason,
        by,
        at: serverTimestamp(),
      });

      alert(`✅ Request ${finalType}`);
      onStatus(rec, finalType);
      closeDecision();
      onClose();
    } catch (e) {
      console.error("[decision] error", e);
      alert("❌ Failed: " + (e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function generateAndAttach() {
    try {
      setBusy(true);

      // Build PDF (jsPDF instance)
      const pdf = await makeReplyPDF({
        letterheadUrl: letterheadUrl || null,
        requestId: rec.requestId,
        dateStr: new Date().toLocaleDateString(),
        toName: rec.memberName || "Member",
        subject: rec.subject || "-",
        body,
        officer: { name: "CIO Officer", designation: "Chief Information Officer" },
      });

      // Local download (instant)
      try {
        const filename = `Reply_${rec.requestId}.pdf`;
        pdf.save(filename);
      } catch {}

      // Upload to Storage and update Firestore
      const blob = pdf.output("blob");
      const path = `cio/replies/${rec.requestId}/Reply_${Date.now()}.pdf`;
      const r = ref(storage, path);
      await uploadBytes(r, blob, { contentType: "application/pdf" });
      const url = await getDownloadURL(r);

      await updateDoc(doc(db,"requests",rec._id), {
        replyDocURL: url,
        cioRemarks: remarks || "",
        lastReplyAt: new Date().toISOString(),
      });

      await addDoc(collection(db,"requests",rec._id,"audits"), {
        action: "reply:pdf",
        url,
        by: auth?.currentUser?.email || "admin",
        at: serverTimestamp()
      });

      alert("✅ Reply PDF generated, downloaded, and uploaded.");
    } catch (e) {
      console.error("[CIO Reply] error", e);
      alert("❌ Failed: " + (e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  const canProgress = rec.status === "New";
  const canDecide = rec.status === "In Progress";

  return (
    <div
      className="fixed inset-0 z-[1000] flex"
      style={{ paddingTop: "var(--header-total,148px)" }}
    >
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div
        className="w-full max-w-xl bg-white overflow-auto border-l rounded-tl-xl shadow-xl"
        style={{ maxHeight: "calc(100vh - var(--header-total,148px))" }}
      >
        {/* Header */}
        <div className="h-14 border-b flex items-center justify-between px-4">
          <div className="font-bold">
            {rec.requestId} • <StatusBadge s={rec.status} />
          </div>
          <button onClick={onClose} className="text-slate-600">✕</button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3 text-sm">
          <div className="bg-slate-50 rounded-xl p-3 border">
            <div className="font-semibold">{rec.subject}</div>
            <div className="text-slate-600 whitespace-pre-wrap">{rec.description}</div>
            <div className="text-xs text-slate-500">
              By {rec.memberName} • {rec.email} • {rec.mobile}
            </div>
          </div>

          {Array.isArray(rec.attachments) && rec.attachments.length > 0 && (
            <div className="bg-white rounded-xl p-3 border">
              <div className="font-semibold mb-1">Attachments</div>
              <ul className="list-disc pl-5">
                {rec.attachments.map((a,i)=>(
                  <li key={i}><a className="text-indigo-700" href={a.url} target="_blank" rel="noreferrer">{a.name || a.url}</a></li>
                ))}
              </ul>
            </div>
          )}

          {/* Transition controls */}
          <div className="flex flex-wrap gap-2">
            {canProgress && (
              <button disabled={busy} onClick={()=>onStatus(rec,"In Progress")}
                      className="px-3 py-2 rounded border bg-amber-50 border-amber-200 text-amber-800">
                Mark In-Progress
              </button>
            )}
            {canDecide && (
              <>
                <button disabled={busy} onClick={generateAndAttach}
                        className={`px-3 py-2 rounded text-white ${busy?"bg-blue-400":"bg-blue-600 hover:bg-blue-700"}`}>
                  Generate Reply PDF
                </button>
                <button disabled={busy} onClick={()=>openDecision("close")}
                        className="px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white">
                  Close (with Reason)
                </button>
                <button disabled={busy} onClick={()=>openDecision("reject")}
                        className="px-3 py-2 rounded bg-rose-600 hover:bg-rose-700 text-white">
                  Reject (with Reason)
                </button>
              </>
            )}
          </div>

          {/* Reply composer (optional) */}
          <div className="bg-white rounded-xl border p-3">
            <div className="font-semibold mb-2">Reply</div>
            <label className="text-xs">Letterhead Image URL (optional)</label>
            <input
              value={letterheadUrl}
              onChange={e=>setLetterheadUrl(e.target.value)}
              placeholder="https://.../letterhead.png"
              className="w-full border rounded px-3 py-2 mb-2"
            />
            <label className="text-xs">Body</label>
            <textarea
              rows={6}
              value={body}
              onChange={e=>setBody(e.target.value)}
              className="w-full border rounded px-3 py-2 mb-2"
            />
            <label className="text-xs">CIO Remarks (register)</label>
            <input
              value={remarks}
              onChange={e=>setRemarks(e.target.value)}
              className="w-full border rounded px-3 py-2 mb-3"
            />

            <div className="text-xs text-slate-500">
              Note: Close/Reject करते समय reason अलग modal में capture होगा और public portal में दिखेगा.
            </div>

            {rec.replyDocURL && (
              <div className="mt-2">
                <a href={rec.replyDocURL} target="_blank" rel="noreferrer"
                   className="text-indigo-700 text-sm underline">View current PDF</a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Decision modal */}
      <DecisionModal
        type={decisionType}
        open={!!decisionType}
        onClose={closeDecision}
        onSubmit={submitDecision}
      />
    </div>
  );
}
